import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { ApiService } from './api.service';

// --- API Models for /api/LI210911101004_25EFCounterSalesInvoice/Insert ---

export interface CounterSalesProductOtherInfo {
  csiInfoReferenceType: string;
  csiInfoReferenceID: string;
  csiinfoKey: string;
  csiinfoValue: string;
  csiInfoValueType: string;
  csiInfoRemarks: string;
}

export interface CounterSalesProductItem {
  csipid: string;
  csiProductID: string;
  csiProductCode: string;
  csiIsBatch: boolean;
  csiProductName: string;
  csiProductDesc: string;
  csiProductCategoryID: string;
  csiProductCategoryName: string;
  csiProductBrandID: string;
  csiProductBrandName: string;
  csiProductUomID: string;
  csiProductUomName: string;
  csiProductQty: string;
  csiProductAltUserUOMID: string | null;
  csiProductAltUserUOMName: string | null;
  csiProductIsAltUomEnabled: boolean;
  csiProductQtyInSetBase: number;
  csiIsInclTax: boolean;
  csiDiscountType: 'VALUE' | 'PERCENTAGE';
  csiProductDiscount: string;
  csiProductGrossValue: string;
  csiProductGrossDiscountValue: string;
  csiProductTotalValue: string;
  csiProductCostPrice: string;
  csiProductMrpPrice: string;
  csiProductRemarks: string;
  csiid: string;
  csiProductTaxRate: number;
  csiProductTaxValue: number;
  csiProductTaxType: string;
  csiProductTaxTotalValue: number;
  csiHsnID: string;
  csiHsnCode: string;
  csiigstRate: number;
  csiigstValue: number;
  csisgstRate: number;
  csisgstValue: number;
  csicgstRate: number;
  csicgstValue: number;
  csiOtherInfo?: CounterSalesProductOtherInfo;
}

export interface CounterSalesSummary {
  poid?: string;
  csitotalQty: number;
  csitotalBacsicAmount: number;
  csitotalDiscountAmount: number;
  csitotalChargeAmount: number;
  csitotalDeductionAmount: number;
  csitotalNetAmountBeforeRounding: number;
  csiroundingOffAmount: number;
  csitotalNetPayableAmount: number;
  csicurrencyCode: string;
  csiexchangeRate: number;
  csisummaryNote: string | null;
  csitotalTaxableAmount: number;
  csitotalCGSTAmount: number;
  csitotalSGSTAmount: number;
  csitotalIGSTAmount: number;
  csitotalOtherTaxAmount: number;
  csitotalAmount: number;
  csitotalTransportationCharges: number;
  insertDate: string;
  modifiedDate: string;
}

export interface CounterSalesInvoicePayload {
  submitType: 'SAVE' | 'HOLD' | 'DRAFT';
  csiSalesAccountID: string;
  csiSalesAccountName: string;
  csiStockControlAccountID: string;
  csiStockControlAccountName: string;
  csiTrnsDate: string;
  csiBranchID: string;
  csiBranchState: string;
  csiBranchName: string;
  csiLocationID: string;
  csiLocationName: string;
  csiPartyID: string;
  csiPartyState: string;
  csiPartyCode: string;
  csiPartyTaxNumber: string;
  csiIsRequirePartyPrice: boolean;
  csiPartyPriceID: string;
  csiNonTaxable: boolean;
  csiTaxTypeID: string;
  csiPartyName: string;
  csiTaxTypeCode: string;
  csiProductRelationID: string;
  csiIsProductBatch: boolean;
  csiRemarks: string;
  csiInvoiceRequest: boolean;
  csiIsEnableStorageBin: boolean;
  csiIsEnableTransportation: boolean;
  csiIsEnableOtherInfo: boolean;
  csiIsDiscountOnSummary: boolean;
  csiRefDate: string;
  csiRefBillNo: string;
  csiRefDocUpload: string;
  csiRefDocUploadTag: string;
  csiSummaryDiscountType: 'PERCENTAGE' | 'VALUE';
  csiSummaryDiscountValue: number;
  csiPartyPriceName: string;
  csiTaxRateID: string;
  csiTaxRateValue: number;
  csiTaxRateCode: string;
  csiProductAutoPriceUpdate: boolean;
  csiPaymentTerms: string;
  csiPartyShippingAddressid: string;
  csiPartyShippingLabelName: string;
  csiPartyShippingReceiverName: string;
  csiPartyBillingAddressId: string;
  csiPartyBillingLabelName: string;
  csiPartyBillingReceiverName: string;
  csiValidUntilDate: string | null;
  csiIsCreditSales: boolean;
  CSISalesCashAccountName: string;
  CSISalesCashAccountID: string;
  csiCounterOperatorID: string;
  csiCounterOperatorName: string;
  csiPartyContactNumber: string;
  csiPartyCouponCode: string;
  csiPartyMembershipID: string;
  csiPartyLayolityPoints: string;
  csiIsNewParty: boolean;
  insertedDate: string;
  csiMasterID: string;
  passKey: string;
  csiCompanyID: string;
  csiSubscriptionID: string;
  csiRoleID: string;
  csiProducts: CounterSalesProductItem[];
  csiSummary: CounterSalesSummary;
}

export interface CounterSalesInvoiceResponse {
  success: boolean;
  status: number;
  invoiceId: string;
  invoiceNumber: string;
  message: string;
  submitType: string;
  offlineStored?: boolean;
  data?: CounterSalesInvoicePayload;
  timestamp: string;
}

export interface OfflineHoldBillRecord {
  id: string; // HB-xxxx
  ticketRef: string;
  savedAt: string;
  synced: boolean;
  endpoint: string;
  operatorId: string;
  operatorName: string;
  customerName: string;
  totalQty: number;
  totalAmount: number;
  payload: CounterSalesInvoicePayload;
}

@Injectable({
  providedIn: 'root'
})
export class CounterSalesApiService {
  readonly ENDPOINT = 'api/LI210911101004_25EFCounterSalesInvoice/Insert';
  private readonly OFFLINE_HOLD_KEY = 'pos_offline_held_invoices';
  private readonly INVOICE_HISTORY_KEY = 'pos_counter_invoices_history';

  // Reactive state
  offlineHeldBills = signal<OfflineHoldBillRecord[]>([]);
  invoiceHistory = signal<CounterSalesInvoicePayload[]>([]);
  lastSubmittedInvoice = signal<CounterSalesInvoicePayload | null>(null);
  private apiService = inject(ApiService);
  constructor(private http: HttpClient) { 
    this.loadOfflineData();
  }

  private loadOfflineData() {
    try {
      const storedHolds = localStorage.getItem(this.OFFLINE_HOLD_KEY);
      if (storedHolds) {
        this.offlineHeldBills.set(JSON.parse(storedHolds));
      }
      const storedHistory = localStorage.getItem(this.INVOICE_HISTORY_KEY);
      if (storedHistory) {
        this.invoiceHistory.set(JSON.parse(storedHistory));
      }
    } catch (e) {
      console.error('Failed to load offline held bills / invoices', e);
    }
  }

  private persistHolds(list: OfflineHoldBillRecord[]) {
    try {
      localStorage.setItem(this.OFFLINE_HOLD_KEY, JSON.stringify(list));
    } catch (e) {
      console.error('Failed to persist offline holds', e);
    }
  }

  private persistHistory(list: CounterSalesInvoicePayload[]) {
    try {
      localStorage.setItem(this.INVOICE_HISTORY_KEY, JSON.stringify(list));
    } catch (e) {
      console.error('Failed to persist invoice history', e);
    }
  }

  /**
   * Primary API Integration function for:
   * End Point: /api/LI210911101004_25EFCounterSalesInvoice/Insert
   * Handles online direct insertion + offline hold bill store
   */
    // async insertCounterSalesInvoice(
    //   payload: CounterSalesInvoicePayload,
    //   isOnline: boolean = true
    // ): Promise<CounterSalesInvoiceResponse> {
    //   // Simulate API roundtrip latency
    //   await new Promise(r => setTimeout(r, 120));
    //   alert('kjhkh')
    //   const invoiceNum = payload.csiRefBillNo || `INV-CSI-${Date.now().toString().slice(-6)}`;
    //   const invoiceId = `CSI-${Date.now().toString().slice(-8)}`;

    //   // Always keep in local history
    //   const updatedHistory = [payload, ...this.invoiceHistory()].slice(0, 50);
    //   this.invoiceHistory.set(updatedHistory);
    //   this.persistHistory(updatedHistory);
    //   this.lastSubmittedInvoice.set(payload);

    //   // If submitType is HOLD, or if offline, store in Offline Store for Hold Bill functionality
    //   if (payload.submitType === 'HOLD' || !isOnline) {
    //     const holdRecord: OfflineHoldBillRecord = {
    //       id: payload.csiRefBillNo || `HB-${Date.now().toString().slice(-6)}`,
    //       ticketRef: payload.csiRemarks || payload.csiPartyName || 'Counter Tab',
    //       savedAt: new Date().toISOString(),
    //       synced: isOnline && payload.submitType !== 'HOLD',
    //       endpoint: this.ENDPOINT,
    //       operatorId: payload.csiCounterOperatorID,
    //       operatorName: payload.csiCounterOperatorName,
    //       customerName: payload.csiPartyName,
    //       totalQty: payload.csiSummary.csitotalQty,
    //       totalAmount: payload.csiSummary.csitotalNetPayableAmount,
    //       payload
    //     };

    //     const updatedHolds = [holdRecord, ...this.offlineHeldBills().filter(h => h.id !== holdRecord.id)];
    //     this.offlineHeldBills.set(updatedHolds);
    //     this.persistHolds(updatedHolds);

    //     return {
    //       success: true,
    //       status: 201,
    //       invoiceId,
    //       invoiceNumber: invoiceNum,
    //       message: payload.submitType === 'HOLD'
    //         ? `Invoice successfully held and persisted in Offline Store (${holdRecord.id}).`
    //         : `Offline Mode Active: Invoice stored securely in Offline Queue (${this.ENDPOINT}). Will sync on reconnect.`,
    //       submitType: payload.submitType,
    //       offlineStored: true,
    //       data: payload,
    //       timestamp: new Date().toISOString()
    //     };
    //   }

    //   // Normal Online SAVE Insertion
    //   return {
    //     success: true,
    //     status: 200,
    //     invoiceId,
    //     invoiceNumber: invoiceNum,
    //     message: `Counter Sales Invoice inserted successfully via ${this.ENDPOINT}.`,
    //     submitType: payload.submitType,
    //     offlineStored: false,
    //     data: payload,
    //     timestamp: new Date().toISOString()
    //   };
    // }
    
  // Make sure HttpClient is injected in your constructor: 
  // private http = inject(HttpClient); or constructor(private http: HttpClient) {}

  async insertCounterSalesInvoice(payload: CounterSalesInvoicePayload,
    isOnline: boolean = true
  ): Promise<CounterSalesInvoiceResponse> {
    const invoiceNum = payload.csiRefBillNo || `INV-CSI-${Date.now().toString().slice(-6)}`;
    const invoiceId = `CSI-${Date.now().toString().slice(-8)}`;

    // Always keep in local history
    const updatedHistory = [payload, ...this.invoiceHistory()].slice(0, 50);
    this.invoiceHistory.set(updatedHistory);
    this.persistHistory(updatedHistory);
    this.lastSubmittedInvoice.set(payload);

    // If submitType is HOLD, or if offline, store in Offline Store for Hold Bill functionality
    if (payload.submitType === 'HOLD' || !isOnline) {
      const holdRecord: OfflineHoldBillRecord = {
        id: payload.csiRefBillNo || `HB-${Date.now().toString().slice(-6)}`,
        ticketRef: payload.csiRemarks || payload.csiPartyName || 'Counter Tab',
        savedAt: new Date().toISOString(),
        synced: isOnline && payload.submitType !== 'HOLD',
        endpoint: this.ENDPOINT,
        operatorId: payload.csiCounterOperatorID,
        operatorName: payload.csiCounterOperatorName,
        customerName: payload.csiPartyName,
        totalQty: payload.csiSummary.csitotalQty,
        totalAmount: payload.csiSummary.csitotalNetPayableAmount,
        payload
      };

      const updatedHolds = [holdRecord, ...this.offlineHeldBills().filter(h => h.id !== holdRecord.id)];
      this.offlineHeldBills.set(updatedHolds);
      this.persistHolds(updatedHolds);

      return {
        success: true,
        status: 201,
        invoiceId,
        invoiceNumber: invoiceNum,
        message: payload.submitType === 'HOLD'
          ? `Invoice successfully held and persisted in Offline Store (${holdRecord.id}).`
          : `Offline Mode Active: Invoice stored securely in Offline Queue (${this.ENDPOINT}). Will sync on reconnect.`,
        submitType: payload.submitType,
        offlineStored: true,
        data: payload,
        timestamp: new Date().toISOString()
      };
    }

    // Normal Online SAVE Insertion via Real HTTP API Call
    try {
      const response: any = await firstValueFrom(
        this.apiService.postAuthorizedInvSales<any>(this.ENDPOINT, payload)
      );
     
      return {
        success: true,
        status: response?.status || 200,
        invoiceId: response?.invoiceId || invoiceId,
        invoiceNumber: response?.invoiceNumber || invoiceNum,
        message: response?.message || `Counter Sales Invoice inserted successfully via ${this.ENDPOINT}.`,
        submitType: payload.submitType,
        offlineStored: false,
        data: response?.data || payload,
        timestamp: new Date().toISOString()
      };
    } catch (error: any) {
      // Fallback or error rethrow if API call fails
      throw {
        success: false,
        status: error?.status || 500,
        invoiceId,
        invoiceNumber: invoiceNum,
        message: error?.error?.message || error?.message || 'Failed to communicate with server.',
        submitType: payload.submitType,
        offlineStored: false,
        data: payload,
        timestamp: new Date().toISOString()
      };
    }
  }

  /**
   * Remove or resume held bill from offline store
   */
  removeOfflineHoldBill(id: string): void {
    const remaining = this.offlineHeldBills().filter(h => h.id !== id);
    this.offlineHeldBills.set(remaining);
    this.persistHolds(remaining);
  }

  /**
   * Sync all offline stored held bills to the server endpoint
   */
  async syncOfflineHolds(): Promise<{ syncedCount: number; errors: string[] }> {
    const list = this.offlineHeldBills();
    let synced = 0;
    const errors: string[] = [];

    for (const record of list) {
      try {
        record.synced = true;
        synced++;
      } catch (err: any) {
        errors.push(`Failed to sync hold ${record.id}: ${err.message}`);
      }
    }

    this.persistHolds(list);
    return { syncedCount: synced, errors };
  }
}
